/**
 * CORE-225 — offline events can now run for minutes, hours, or whole days
 * (retreats, festivals, intensives). Duration itself is still stored the way
 * the whole codebase already models it — as the delta between an event's
 * `startDateUtc` and `endDateUtc` — so there is no new column and no drift
 * between a "unit" field and the actual timestamps. This module is the single
 * place that (a) turns the create-form's value + unit into minutes, and
 * (b) turns a minute-delta back into a human, correctly-pluralised Russian
 * label for every display surface (card, calendar detail, preview).
 *
 * Kept RN-free so the formatting is unit-testable in a plain node jest env.
 */

export type DurationUnit = 'minutes' | 'hours' | 'days';

const MINUTES_PER_HOUR = 60;
const MINUTES_PER_DAY = 60 * 24;

/** Russian plural picker: pluralRu(2, 'час', 'часа', 'часов') → 'часа'. */
function pluralRu(n: number, one: string, few: string, many: string): string {
  const abs = Math.abs(n) % 100;
  const tail = abs % 10;
  if (abs > 10 && abs < 20) return many;
  if (tail > 1 && tail < 5) return few;
  if (tail === 1) return one;
  return many;
}

/** Convert the create-form's numeric value + chosen unit into total minutes. */
export function durationToMinutes(value: number, unit: DurationUnit): number {
  const v = Number.isFinite(value) ? Math.max(0, Math.round(value)) : 0;
  if (unit === 'days') return v * MINUTES_PER_DAY;
  if (unit === 'hours') return v * MINUTES_PER_HOUR;
  return v;
}

/**
 * Inverse of the formatter, used to re-hydrate the edit form: pick the same
 * value + unit the label would show, so a 14-day event opens as "14" + "Дни"
 * rather than "20160" + "Минуты".
 */
export function deriveDurationValueUnit(minutes: number): { value: number; unit: DurationUnit } {
  const m = Math.max(0, Math.round(minutes || 0));
  if (m >= MINUTES_PER_DAY && m % MINUTES_PER_DAY === 0) {
    return { value: m / MINUTES_PER_DAY, unit: 'days' };
  }
  if (m >= 2 * MINUTES_PER_HOUR && m % MINUTES_PER_HOUR === 0) {
    return { value: m / MINUTES_PER_HOUR, unit: 'hours' };
  }
  return { value: m, unit: 'minutes' };
}

/**
 * Format a minute-delta as a natural Russian duration label. The unit is
 * chosen so the common cases read the way a master entered them:
 *   90   → "90 минут"      (not a whole hour)
 *   120  → "2 часа"        (whole hours, ≥ 2h)
 *   60   → "60 минут"      (kept as minutes so the 60-min default isn't
 *                           silently rewritten to "1 час")
 *   2880 → "2 дня"         (whole days)
 *   20160→ "14 дней"
 */
export function formatEventDuration(minutes: number | null | undefined): string {
  const m = Math.max(0, Math.round(minutes || 0));
  if (m <= 0) return `0 ${pluralRu(0, 'минута', 'минуты', 'минут')}`;
  if (m >= MINUTES_PER_DAY && m % MINUTES_PER_DAY === 0) {
    const days = m / MINUTES_PER_DAY;
    return `${days} ${pluralRu(days, 'день', 'дня', 'дней')}`;
  }
  if (m >= 2 * MINUTES_PER_HOUR && m % MINUTES_PER_HOUR === 0) {
    const hours = m / MINUTES_PER_HOUR;
    return `${hours} ${pluralRu(hours, 'час', 'часа', 'часов')}`;
  }
  return `${m} ${pluralRu(m, 'минута', 'минуты', 'минут')}`;
}

/** Russian label for a unit, used by the create-form's unit selector. */
export const DURATION_UNIT_LABEL: Record<DurationUnit, string> = {
  minutes: 'Минуты',
  hours: 'Часы',
  days: 'Дни',
};

export const DURATION_UNIT_ORDER: DurationUnit[] = ['minutes', 'hours', 'days'];
